import { useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Checkbox } from '#app/shared/ui/inputs/Checkbox.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { FuelTypeEnum, EcoLabelEnum } from '../../../../../types/enums'

const etiquetaImages: Record<string, string> = {
  [EcoLabelEnum.ECO]: '/img/Etiquetas_Conbustible/Etiqueta_ECO.png',
  [EcoLabelEnum.C]: '/img/Etiquetas_Conbustible/Etiqueta_C.png',
  [EcoLabelEnum.CERO]: '/img/Etiquetas_Conbustible/Etiqueta_0.png',
  [EcoLabelEnum.B]: '/img/Etiquetas_Conbustible/Etiqueta_B.png',
}

type FilterCombustibleProps = {
  fuelTypeCounts: Record<string, number>
  ecoLabelCounts: Record<string, number>
}

export const FilterCombustible = ({
  fuelTypeCounts,
  ecoLabelCounts,
}: FilterCombustibleProps): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFilterOpen, setIsFilterOpen] = useState<
    'combustible' | 'etiqueta' | null
  >(null)

  const handleCheckboxChange = (
    filterKey: string,
    value: string,
    checked: boolean,
  ) => {
    const currentFilters = searchParams.get(filterKey)?.split(',') || []
    let updatedFilters

    if (checked) {
      updatedFilters = [...currentFilters, value]
    } else {
      updatedFilters = currentFilters.filter((f: string) => f !== value)
    }

    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      [filterKey]: updatedFilters.join(','),
    })
  }

  return (
    <MainContainer>
      <div
        className={`
          flex flex-col

          lg:flex-row
        `}
      >
        <div
          className={`
            w-full

            lg:max-w-[65%]
          `}
        >
          <button
            className={`
              flex w-full items-center justify-between border-t py-4

              lg:hidden
            `}
            onClick={() =>
              setIsFilterOpen(
                isFilterOpen === 'combustible' ? null : 'combustible',
              )
            }
          >
            Combustibles
            <Icon
              name="basic/chevron-down"
              className={`
                h-5 w-5 text-neutral-400 transition duration-200

                ${isFilterOpen === 'combustible' && 'rotate-180'}
              `}
            />
          </button>
          {(isFilterOpen === 'combustible' || window.innerWidth >= 1024) && (
            <div>
              {window.innerWidth >= 1024 && (
                <h3 className="text-base-bold">Combustibles</h3>
              )}
              <div
                className={`
                  grid grid-cols mt-4 gap-x-8 gap-y-4

                  lg:grid-cols-3 lg:gap-x-16 lg:gap-y-2
                `}
              >
                {Object.values(FuelTypeEnum).map((combustible) => {
                  const count = fuelTypeCounts[combustible] ?? 0
                  const isDisabled = count === 0
                  return (
                    <div
                      key={combustible}
                      className="flex items-center gap-2 whitespace-nowrap"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        !isDisabled &&
                        handleCheckboxChange(
                          'combustible',
                          combustible,
                          !searchParams
                            .get('combustible')
                            ?.split(',')
                            .includes(combustible),
                        )
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !isDisabled
                        ) {
                          handleCheckboxChange(
                            'combustible',
                            combustible,
                            !searchParams
                              .get('combustible')
                              ?.split(',')
                              .includes(combustible),
                          )
                        }
                      }}
                    >
                      <Checkbox
                        id={combustible}
                        checked={
                          searchParams
                            .get('combustible')
                            ?.split(',')
                            .includes(combustible) || false
                        }
                        disabled={isDisabled}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(
                            'combustible',
                            combustible,
                            !!checked,
                          )
                        }
                      />
                      <label
                        htmlFor={combustible}
                        className={isDisabled ? `text-neutral-500` : ''}
                      >
                        {combustible} ({count})
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div
          className={`
            w-full

            lg:max-w-[25%]
          `}
        >
          <button
            className={`
              flex w-full items-center justify-between border-t py-4

              lg:hidden
            `}
            onClick={() =>
              setIsFilterOpen(isFilterOpen === 'etiqueta' ? null : 'etiqueta')
            }
          >
            Etiquetas Medioambientales
            <Icon
              name="basic/chevron-down"
              className={`
                h-5 w-5 text-neutral-400 transition duration-200

                ${isFilterOpen === 'etiqueta' && 'rotate-180'}
              `}
            />
          </button>
          {(isFilterOpen === 'etiqueta' || window.innerWidth >= 1024) && (
            <div>
              {window.innerWidth >= 1024 && (
                <h3 className="mb-4 text-base-bold">Etiqueta Medioambiental</h3>
              )}
              <div className="flex flex-col gap-2">
                {Object.values(EcoLabelEnum).map((etiqueta) => {
                  const count = ecoLabelCounts[etiqueta] ?? 0
                  const imageSrc = etiquetaImages[etiqueta]

                  const isSelected = searchParams
                    .get('etiqueta')
                    ?.split(',')
                    .includes(etiqueta)
                  const isDisabled = count === 0

                  return (
                    <div
                      key={etiqueta}
                      className={`
                        flex cursor-pointer items-center gap-2 p-2

                        hover:bg-primary-50

                        ${isSelected ? 'bg-primary-50' : ''}
                        ${isDisabled ? 'text-neutral-500' : ''}
                      `}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        !isDisabled &&
                        handleCheckboxChange('etiqueta', etiqueta, !isSelected)
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !isDisabled
                        ) {
                          handleCheckboxChange(
                            'etiqueta',
                            etiqueta,
                            !isSelected,
                          )
                        }
                      }}
                    >
                      <img src={imageSrc} alt={etiqueta} className="h-6 w-6" />
                      <label
                        htmlFor={etiqueta}
                        className={count === 0 ? `text-neutral-500` : ''}
                      >
                        {etiqueta} ({count})
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  )
}
